import * as _ from 'lodash-es';
import { MoveStates, PostStatus, UNORGANIZED_TOPIC_ID } from '../constants';
import isReorganizationPostEdgePinned from './isReorganizationPostEdgePinned';
import type { ReorganizeOperation } from '../../graphql/generated/graphql';
import { ReorganizeOperationName } from '../../graphql/generated/graphql';
import type { ReorganizationPostEdge } from '../useTopicReorganization';

function getPostStatusOperations(
  status: PostStatus,
  postId: string,
): ReorganizeOperation | null {
  switch (status) {
    case PostStatus.deleted:
      return {
        operationName: ReorganizeOperationName.DeletePost,
        id: postId,
      };
    case PostStatus.archived:
      return {
        operationName: ReorganizeOperationName.UpdatePost,
        id: postId,
        archived: status === PostStatus.archived,
      };
    default:
      return null;
  }
}

interface GetPostsOperationsArgs {
  parentId: string;
  reorganizationPostEdges: ReorganizationPostEdge[];
}

const getPostsOperations = ({
  parentId,
  reorganizationPostEdges,
}: GetPostsOperationsArgs): ReorganizeOperation[] => {
  const moveAndPinOperations = reorganizationPostEdges.flatMap(
    (edge, index) => {
      const operations: ReorganizeOperation[] = [];

      const {
        pinnedOrder,
        postId,
        reorganizationMetadata: { move, isPinned },
      } = edge;

      const state = move?.state;

      if (state === MoveStates.movedOut && parentId !== UNORGANIZED_TOPIC_ID) {
        operations.push({
          operationName: ReorganizeOperationName.RemoveTopicFromPost,
          postId,
          topicId: parentId,
        });
      }

      if (state === MoveStates.movedIn) {
        operations.push({
          operationName: ReorganizeOperationName.AddTopicToPost,
          postId,
          topicId: parentId,
        });
      }

      if (state === MoveStates.initial && isPinned === false) {
        operations.push({
          operationName: ReorganizeOperationName.UnpinPost,
          postId,
          topicId: parentId,
        });
      }

      if (
        isReorganizationPostEdgePinned(edge) &&
        (state === MoveStates.movedIn || index !== pinnedOrder)
      ) {
        operations.push({
          operationName: ReorganizeOperationName.PinPost,
          postId,
          topicId: parentId,
          pinnedOrder: index,
        });
      }

      return operations;
    },
  );

  const postStatusOperations = _.compact(
    reorganizationPostEdges.map((edge) => {
      return getPostStatusOperations(
        edge.reorganizationMetadata.status,
        edge.postId,
      );
    }),
  );

  return [...moveAndPinOperations, ...postStatusOperations];
};

export default getPostsOperations;
